import { tenantRoutes } from '@/router/modules/routing.js'
import { getLoginUser } from '@/utils/loginUser.js'

export const ROLE_SUPER_ADMIN = 99
export const ROLE_NORMAL_ADMIN = 98

const EXCLUDE_PERMISSIONS = new Set(['platform', 'captain'])
const SYSTEM_USER_LIST_PATH = '/system'

const DETAIL_ROUTE_PARENT = {
  '/machinist-detail': '/Machinist',
  '/captain-detail': '/Captain',
  '/equipment/device-detail': '/equipment/device',
}

export function isSuperAdminRole(roleId) {
  return Number(roleId) === ROLE_SUPER_ADMIN
}

export function isNormalAdminRole(roleId) {
  return Number(roleId) === ROLE_NORMAL_ADMIN
}

export function isTenantAdminRole(roleId) {
  const rid = Number(roleId)
  return rid === ROLE_SUPER_ADMIN || rid === ROLE_NORMAL_ADMIN
}

export function buildAdminMenuPermissionTree() {
  return tenantRoutes
    .filter((route) => route.show !== false && !EXCLUDE_PERMISSIONS.has(route.permission))
    .map((route) => {
      const children = (route.children || [])
        .filter((child) => child.show !== false)
        .map((child) => ({ id: child.path, label: child.title }))
      if (children.length) {
        return { id: route.path, label: route.title, children }
      }
      return { id: route.path, label: route.title }
    })
}

export function getAllAdminMenuPermissionKeys() {
  const keys = []
  tenantRoutes
    .filter((route) => route.show !== false && !EXCLUDE_PERMISSIONS.has(route.permission))
    .forEach((route) => {
      const children = (route.children || []).filter((child) => child.show !== false)
      if (children.length) {
        children.forEach((child) => keys.push(child.path))
      } else {
        keys.push(route.path)
      }
    })
  return [...new Set(keys)]
}

export function getDefaultNormalAdminPermissions() {
  return getAllAdminMenuPermissionKeys().filter((path) => path !== SYSTEM_USER_LIST_PATH)
}

export function parseMenuPermissions(value) {
  if (value == null || value === '') return null
  if (Array.isArray(value)) return value.length ? [...new Set(value)] : null
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) && parsed.length ? [...new Set(parsed)] : null
  } catch {
    return null
  }
}

export function getLoginUserMenuPermissions(user = getLoginUser()) {
  const roleId = Number(user?.roleId ?? user?.role_id ?? 0)
  if (isSuperAdminRole(roleId)) return null
  if (!isNormalAdminRole(roleId)) return null

  const saved = parseMenuPermissions(user?.menuPermissions ?? user?.menu_permissions)
  // 普通管理员：仅展示已保存的菜单；未配置时仅保留首页，避免误显示全部菜单
  return saved?.length ? saved : ['/home']
}

export function filterRoutesByMenuPermissions(routes, permissions) {
  if (permissions == null) return routes
  if (!permissions.length) {
    return routes.filter((route) => route.path === '/home')
  }
  const allowed = new Set(permissions)
  return routes
    .map((route) => {
      if (route.children?.length) {
        const children = route.children.filter((child) => isChildRouteAllowed(child, allowed))
        if (!children.length) return null
        return { ...route, children }
      }
      return allowed.has(route.path) ? route : null
    })
    .filter(Boolean)
}

function isChildRouteAllowed(child, allowed) {
  if (allowed.has(child.path)) return true
  for (const key of allowed) {
    if (child.path.startsWith(`${key}/`)) return true
  }
  return false
}

export function applyMenuPermissionToRoute(route, permissions) {
  if (!route) return route
  if (permissions == null) return route
  if (!permissions.length) return null
  const allowed = new Set(permissions)
  if (route.children?.length) {
    const children = route.children.filter((child) => isChildRouteAllowed(child, allowed))
    if (!children.length) return null
    return { ...route, children }
  }
  return allowed.has(route.path) ? route : null
}

export function isPathAllowedForUser(path, user = getLoginUser()) {
  const roleId = Number(user?.roleId ?? user?.role_id ?? 0)
  if (isSuperAdminRole(roleId)) return true
  if (!isNormalAdminRole(roleId)) return true
  if (path === '/home') return true

  const permissions = getLoginUserMenuPermissions(user)
  if (!permissions?.length) return false

  const allowed = new Set(permissions)
  if (allowed.has(path)) return true

  const detailParent = DETAIL_ROUTE_PARENT[path]
  if (detailParent && allowed.has(detailParent)) return true

  for (const item of permissions) {
    if (path.startsWith(`${item}/`)) return true
  }
  return false
}

export function normalizeMenuPermissionPayload(checkedKeys) {
  const allowed = new Set(getAllAdminMenuPermissionKeys())
  return [...new Set(checkedKeys)].filter((key) => allowed.has(key))
}

export function resolveCheckedMenuKeys(savedPermissions) {
  const effective = savedPermissions?.length
    ? savedPermissions
    : getDefaultNormalAdminPermissions()
  const saved = new Set(effective)
  const checked = []
  buildAdminMenuPermissionTree().forEach((node) => {
    if (node.children?.length) {
      node.children.forEach((child) => {
        if (saved.has(child.id)) checked.push(child.id)
      })
      if (node.children.every((child) => saved.has(child.id))) {
        checked.push(node.id)
      }
    } else if (saved.has(node.id)) {
      checked.push(node.id)
    }
  })
  return checked
}
